import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { STATUS } from "@/constants";
import { apiFetch } from "@/shared/api/apiFetch";
import { env } from "@/shared/config/env";
import { isAdminHostname } from "@/shared/tenant/getTenantFromHostname";

// ─── Response types ─────────────────────────────────────────────

/** Payload from `GET /v1/tenants/configuration` (Cognito-hosted UI only). */
export type TenantConfiguration = {
  cognito_user_pool_id: string;
  cognito_app_client_id: string;
  cognito_domain: string;
  /**
   * Whether the tenant has turned off email/password sign-in. Served unauthenticated because the
   * login redirect needs it before anyone has signed in. Optional so an older backend, or the
   * admin-hostname branch below, simply reads as "not disabled".
   */
  credential_login_disabled?: boolean;
};

// ─── Slice state ────────────────────────────────────────────────

export type TenantConfigurationState = {
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  configurations: TenantConfiguration | null;
};

export const tenantConfigurationInitialState: TenantConfigurationState = {
  status: STATUS.IDLE,
  error: null,
  configurations: null,
};

// ─── Thunk ──────────────────────────────────────────────────────

export const fetchTenantConfiguration = createAsyncThunk<
  TenantConfiguration,
  void,
  { rejectValue: string }
>(
  "tenantConfiguration/fetch",
  async (_, { rejectWithValue }) => {
    if (isAdminHostname(window.location.hostname)) {
      const cognitoUserPoolId = env.cognitoUserPoolId.trim();
      const cognitoAppClientId = env.cognitoAppClientId.trim();
      const cognitoDomain = env.cognitoDomain.trim();
      if (!cognitoUserPoolId || !cognitoAppClientId || !cognitoDomain) {
        return rejectWithValue(
          "Missing admin Cognito env. Set VITE_COGNITO_USER_POOL_ID, VITE_COGNITO_APP_CLIENT_ID, and VITE_COGNITO_DOMAIN.",
        );
      }
      return {
        cognito_user_pool_id: cognitoUserPoolId,
        cognito_app_client_id: cognitoAppClientId,
        cognito_domain: cognitoDomain,
      };
    }

    try {
      const res = await apiFetch("/v1/tenants/configuration", { method: "GET" });
      if (!res.ok) {
        return rejectWithValue(`Request failed (${res.status})`);
      }
      return (await res.json()) as TenantConfiguration;
    } catch (e) {
      const message = e instanceof Error ? e.message : "Request failed";
      return rejectWithValue(message);
    }
  },
  {
    condition(_, { getState }) {
      const { status } = (
        getState() as { tenantConfiguration: TenantConfigurationState }
      ).tenantConfiguration;
      return status !== STATUS.LOADING && status !== STATUS.SUCCEEDED;
    },
  }
);

// ─── Slice ──────────────────────────────────────────────────────

const tenantConfigurationSlice = createSlice({
  name: "tenantConfiguration",
  initialState: tenantConfigurationInitialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchTenantConfiguration.pending, (state) => {
        state.status = STATUS.LOADING;
        state.error = null;
      })
      .addCase(fetchTenantConfiguration.fulfilled, (state, action) => {
        state.status = STATUS.SUCCEEDED;
        state.configurations = action.payload;
      })
      .addCase(fetchTenantConfiguration.rejected, (state) => {
        state.status = STATUS.FAILED;
        state.configurations = null;
        state.error = null;
      });
  },
});

export const tenantConfigurationReducer = tenantConfigurationSlice.reducer;

// ─── Selectors ──────────────────────────────────────────────────

const selectTenantConfiguration = (
  state: { tenantConfiguration: TenantConfigurationState },
) => state.tenantConfiguration.configurations;

export const selectCognitoConfig = createSelector(
  [selectTenantConfiguration],
  (configuration) => {
    const cognitoDomain = configuration?.cognito_domain?.trim();
    if (!configuration || !cognitoDomain) return null;
    return {
      userPoolId: configuration.cognito_user_pool_id,
      clientId: configuration.cognito_app_client_id,
      cognitoDomain,
    };
  },
);

/**
 * Whether this tenant signs in through an identity provider only.
 *
 * With email/password off, the app client has a single identity provider, and Cognito forwards
 * straight to it rather than rendering its sign-in page — so the login redirect asks for that page
 * explicitly. Defaults to false, which leaves password tenants and the admin realm untouched.
 */
export const selectCredentialLoginDisabled = createSelector(
  [selectTenantConfiguration],
  (configuration) => configuration?.credential_login_disabled === true,
);
